import type { ReactNode } from "react";
import { cn } from "@wa/ui";
import { TabRail } from "./TabRail";

export interface PillTab<T extends string> {
  value: T;
  label: string;
  /** Optional count, drawn as a chip after the label. Leave it out for an empty
   *  set: a strip of "0" chips reads as a row of counters that failed, and the
   *  tab's own empty card says the same thing in words. */
  count?: number;
  /** Shown instead of the label when the row runs in `iconOnly` mode. */
  icon?: ReactNode;
}

/**
 * A row of pill tabs. The selected one flips to the inverted surface, which is
 * the same "this is where you are" signal the sidebar uses - one idea, two
 * places, so nobody has to learn a second highlight.
 *
 * `iconOnly` drops the wording and keeps the glyph, for rows where the choice is
 * a shape rather than a subject (grid or list). The label is not thrown away: it
 * becomes the button's accessible name and its tooltip, so a screen reader and a
 * hovering pointer both still get the word.
 *
 * `rail` puts the row on one line that scrolls sideways instead of letting it
 * fold onto a second. Opt in, and it belongs to the strips that name the parts
 * of a record rather than to a toolbar's three filters - see `TabRail`, which
 * carries the argument and the fade.
 */
export function PillTabs<T extends string>({
  tabs,
  value,
  onChange,
  small,
  iconOnly,
  rail,
  className,
}: {
  tabs: readonly PillTab<T>[];
  value: T;
  onChange: (v: T) => void;
  small?: boolean;
  iconOnly?: boolean;
  rail?: boolean;
  className?: string;
}) {
  const pills = tabs.map((tab) => {
    // A tab with no icon has nothing to show in icon mode, so it keeps its
    // wording rather than rendering an empty pill.
    const asIcon = iconOnly && tab.icon;
    return (
      <button
        key={tab.value}
        type="button"
        role="tab"
        aria-selected={tab.value === value}
        aria-label={asIcon ? tab.label : undefined}
        title={asIcon ? tab.label : undefined}
        className={cn(tab.value === value && "is-on")}
        onClick={() => onChange(tab.value)}
      >
        {asIcon ? tab.icon : tab.label}
        {/* No chip in icon mode: the pill there is a fixed square with no
            padding, so a chip would push out of its own shape - and the word it
            would sit beside has already moved to the tooltip. */}
        {!asIcon && typeof tab.count === "number" ? (
          <span className="crm-tab-n">{tab.count}</span>
        ) : null}
      </button>
    );
  });

  // The modifiers go to the scroller, which is the box that carries
  // `.crm-tabs`; the caller's own class goes to the wrapper around it, which is
  // where a margin belongs. `className` is a layout hook at every call site here
  // (`mb-4`, `mb-5`), so putting it on the inner box would put the gap inside
  // the scrolling area instead of under the row.
  if (rail) {
    return (
      <TabRail
        className={className}
        tabsClassName={cn(small && "crm-tabs-sm", iconOnly && "crm-tabs-icon")}
      >
        {pills}
      </TabRail>
    );
  }

  return (
    <div
      className={cn("crm-tabs", small && "crm-tabs-sm", iconOnly && "crm-tabs-icon", className)}
      role="tablist"
    >
      {pills}
    </div>
  );
}
